import { useEffect, useState, useCallback, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Loader2, Plus, X, Trophy, TrendingUp, TrendingDown, Trash2, Paperclip, Image as ImageIcon, Video, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { tInput, tLabel, tBtn, tBtnGhost } from "./trackUi";

const MEDIA = `${process.env.REACT_APP_BACKEND_URL}/api`;
const parseAttempts = (str) => str.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).map((raw) => ({ raw }));

function RecordForm({ orgId, athleteId, tests, onCancel, onSaved }) {
  const [form, setForm] = useState({ test_id: tests[0]?.id || "", date: new Date().toISOString().slice(0, 10), season: "", attempts: "", official_value: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const attempts = parseAttempts(form.attempts);
    if (!attempts.length) { toast.error("Enter at least one attempt."); return; }
    setSaving(true);
    try {
      await api.post("/track/test-results", {
        athlete_id: athleteId, test_id: form.test_id, date: form.date || null, season: form.season || null,
        attempts, official_value: form.official_value === "" ? null : Number(form.official_value), notes: form.notes || null,
      });
      toast.success("Result recorded."); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="bg-[#121212] border border-white/10 p-6 mb-6 space-y-4" data-testid="record-test-form">
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className={tLabel}>Test *</label><select data-testid="record-test-select" required value={form.test_id} onChange={(e) => set("test_id", e.target.value)} className={tInput}>{tests.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
        <div><label className={tLabel}>Date</label><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={tInput} /></div>
        <div><label className={tLabel}>Season</label><input value={form.season} onChange={(e) => set("season", e.target.value)} className={tInput} placeholder="2026" /></div>
      </div>
      <div><label className={tLabel}>Attempts * (comma separated)</label><input data-testid="record-attempts" required value={form.attempts} onChange={(e) => set("attempts", e.target.value)} className={tInput} placeholder="4.62, 4.55, 4.70" /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={tLabel}>Official Value (optional override)</label><input data-testid="record-official" type="number" step="any" value={form.official_value} onChange={(e) => set("official_value", e.target.value)} className={tInput} placeholder="Defaults to best attempt" /></div>
        <div><label className={tLabel}>Notes</label><input value={form.notes} onChange={(e) => set("notes", e.target.value)} className={tInput} /></div>
      </div>
      <div className="flex gap-2">
        <button data-testid="record-submit" disabled={saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Result</button>
        <button type="button" onClick={onCancel} className={tBtnGhost}><X size={14} /> Cancel</button>
      </div>
    </form>
  );
}

function StatCard({ s }) {
  const chartData = s.progression.map((p, i) => ({ i, label: p.date || `#${i + 1}`, value: p.value, display: p.display }));
  const better = s.improvement != null && s.improvement > 0;
  return (
    <div className="border border-white/10 bg-[#121212] p-5" data-testid={`test-stat-${s.test_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="font-display font-black uppercase tracking-tight text-xl">{s.test_name}</p>
        <div className="flex gap-6 flex-wrap">
          <div className="flex items-center gap-2"><Trophy size={16} className="text-white/50" /><div><p className="text-white/40 text-[10px] uppercase tracking-widest">Personal Best</p><p className="font-bold" data-testid={`pb-${s.test_id}`}>{s.pb_display || "—"}</p></div></div>
          <div><p className="text-white/40 text-[10px] uppercase tracking-widest">Latest</p><p className="font-bold">{s.latest_display || "—"}</p></div>
          {s.improvement != null && (
            <div className="flex items-center gap-1.5">{better ? <TrendingUp size={16} className="text-emerald-400" /> : <TrendingDown size={16} className="text-red-400" />}<div><p className="text-white/40 text-[10px] uppercase tracking-widest">vs Previous</p><p className={`font-bold ${better ? "text-emerald-400" : "text-red-400"}`}>{better ? "+" : "-"}{s.improvement_display}</p></div></div>
          )}
        </div>
      </div>
      {chartData.length > 1 && (
        <div style={{ width: "100%", height: 190 }} data-testid={`test-chart-${s.test_id}`}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} domain={["auto", "auto"]} reversed={s.lower_better} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} formatter={(v, n, p) => [p.payload.display, s.test_name]} />
              <Line type="monotone" dataKey="value" stroke="#fff" strokeWidth={2} dot={{ r: 3, fill: "#fff" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {Object.entries(s.season_best || {}).map(([yr, v]) => <span key={yr} className="font-mono-accent text-[10px] tracking-[0.15em] uppercase border border-white/15 px-2 py-1 text-white/60">{yr}: {v.display}</span>)}
      </div>
    </div>
  );
}

function ResultRow({ r, canWrite, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const del = async () => {
    if (!window.confirm("Delete this result? This cannot be undone.")) return;
    try { await api.delete(`/track/test-results/${r.id}`); toast.success("Deleted."); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const kind = file.type.startsWith("video") ? "video" : "photo";
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/track/test-results/${r.id}/attachments?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment added."); onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setUploading(false); }
  };
  const delAtt = async (aid) => {
    try { await api.delete(`/track/test-results/${r.id}/attachments/${aid}`); onChanged(); }
    catch { toast.error("Failed to remove attachment."); }
  };
  return (
    <div className="border-b border-white/5 px-4 py-3" data-testid={`result-row-${r.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-white/40 text-xs w-20">{r.date}</span>
          <span className="font-bold">{r.test_name}</span>
          <span className="text-white/60 text-sm">Official <b className="text-white">{r.official_display}</b>{r.official_source === "manual" ? " (manual)" : ""}</span>
          <span className="text-white/40 text-xs">Best {r.best_display} · Avg {r.avg_display}</span>
          <span className="text-white/30 text-xs">{r.attempts.map((a) => a.display).join(" / ")}</span>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button data-testid={`attach-${r.id}`} onClick={() => fileRef.current?.click()} disabled={uploading} className="text-white/30 hover:text-white transition-colors" title="Add photo/video">{uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}</button>
            <input ref={fileRef} type="file" accept="image/*,video/*" onChange={upload} className="hidden" />
            <button data-testid={`del-result-${r.id}`} onClick={del} className="text-white/25 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
          </div>
        )}
      </div>
      {r.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-3" data-testid={`attachments-${r.id}`}>
          {r.attachments.map((a) => {
            const url = `${MEDIA}/track/test-results/${r.id}/attachments/${a.id}/file`;
            return (
              <div key={a.id} className="relative group border border-white/10">
                {a.kind === "video"
                  ? <video src={url} controls className="w-40 h-24 object-cover bg-black" />
                  : <img src={url} alt={a.filename} className="w-28 h-24 object-cover" />}
                {canWrite && <button onClick={() => delAtt(a.id)} className="absolute top-1 right-1 bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>}
                <span className="absolute bottom-1 left-1 bg-black/70 px-1 py-0.5">{a.kind === "video" ? <Video size={11} /> : <ImageIcon size={11} />}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TrackAthleteTesting({ athlete, orgId, canWrite }) {
  const [stats, setStats] = useState([]);
  const [results, setResults] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecord, setShowRecord] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const org = orgId || athlete.organization_id;
      const [s, r, t] = await Promise.all([
        api.get(`/track/athletes/${athlete.id}/test-stats`),
        api.get(`/track/test-results?athlete_id=${athlete.id}`),
        api.get(`/track/tests${org ? `?organization_id=${org}&status=active` : "?status=active"}`),
      ]);
      setStats(s.data); setResults(r.data); setTests(t.data);
    } catch { toast.error("Failed to load testing data."); } finally { setLoading(false); }
  }, [athlete.id, athlete.organization_id, orgId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="athlete-testing">
      {canWrite && (
        <div className="mb-5"><button data-testid="record-test-btn" onClick={() => setShowRecord((v) => !v)} className={tBtn}>{showRecord ? <X size={14} /> : <Plus size={14} />} {showRecord ? "Cancel" : "Record Test"}</button></div>
      )}
      {showRecord && canWrite && tests.length > 0 && <RecordForm orgId={orgId} athleteId={athlete.id} tests={tests} onCancel={() => setShowRecord(false)} onSaved={() => { setShowRecord(false); load(); }} />}

      {stats.length === 0
        ? <div className="border border-white/10 bg-[#121212] p-8 text-white/40 text-sm text-center" data-testid="testing-empty">No test results recorded yet. {canWrite ? "Record a test to see personal bests, averages and progress charts." : ""}</div>
        : <div className="space-y-6 mb-8">{stats.map((s) => <StatCard key={s.test_id} s={s} />)}</div>}

      {results.length > 0 && (
        <div>
          <p className={tLabel}>Result History</p>
          <div className="border border-white/10 bg-[#121212]" data-testid="result-history">{results.map((r) => <ResultRow key={r.id} r={r} canWrite={canWrite} onChanged={load} />)}</div>
        </div>
      )}
    </div>
  );
}
